import { useState, useMemo } from 'react';
import { Text } from 'foundations-components/components/Text';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import { Link } from 'foundations-components/transitional/components/Link';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { useHistory } from 'quartz-routing/react-router';
import { PaginatedDataTable } from '../PaginatedDataTable';
import { buildQuoteDetailsUrl } from '../../utils/quoteUrls';
import { jsx as _jsx } from "react/jsx-runtime";
const EMPTY_PLACEHOLDER = '--';
function quoteName(quote) {
  var _ref, _quote$title;
  return (_ref = (_quote$title = quote.title) !== null && _quote$title !== void 0 ? _quote$title : quote.quoteNumber) !== null && _ref !== void 0 ? _ref : '';
}
const SORT_COMPARATORS = {
  name: (a, b) => quoteName(a).localeCompare(quoteName(b)),
  type: (a, b) => {
    var _a$type, _b$type;
    return String((_a$type = a.type) !== null && _a$type !== void 0 ? _a$type : '').localeCompare(String((_b$type = b.type) !== null && _b$type !== void 0 ? _b$type : ''));
  },
  acceptedDate: (a, b) => {
    var _a$acceptedDate, _b$acceptedDate;
    return ((_a$acceptedDate = a.acceptedDate) !== null && _a$acceptedDate !== void 0 ? _a$acceptedDate : Infinity) - ((_b$acceptedDate = b.acceptedDate) !== null && _b$acceptedDate !== void 0 ? _b$acceptedDate : Infinity);
  },
  amount: (a, b) => {
    var _a$amount, _b$amount;
    return parseFloat((_a$amount = a.amount) !== null && _a$amount !== void 0 ? _a$amount : '0') - parseFloat((_b$amount = b.amount) !== null && _b$amount !== void 0 ? _b$amount : '0');
  }
};
function sortQuotes(quotes, sort) {
  if (!sort) {
    return quotes;
  }
  const comparator = SORT_COMPARATORS[sort.columnName];
  if (!comparator) {
    return quotes;
  }
  return [...quotes].sort((a, b) => sort.direction * comparator(a, b));
}
const QUOTE_TYPE_MESSAGES = {
  INITIAL: 'quotes.type.initial',
  RENEWAL: 'quotes.type.renewal',
  CHANGE: 'quotes.type.change'
};
function QuoteNameCell({
  row
}) {
  const history = useHistory();
  const name = quoteName(row);
  return /*#__PURE__*/_jsx("td", {
    children: row.quoteLink ? /*#__PURE__*/_jsx(Link, {
      onClick: () => history.push(buildQuoteDetailsUrl(row)),
      children: name
    }) : /*#__PURE__*/_jsx(Text, {
      children: name
    })
  });
}
const COLUMNS = [{
  columnName: 'name',
  sortable: true,
  width: 360,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "quotes.columns.name"
  }),
  CellComponent: QuoteNameCell
}, {
  columnName: 'type',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "quotes.columns.type"
  }),
  CellComponent: ({
    row
  }) => /*#__PURE__*/_jsx("td", {
    children: /*#__PURE__*/_jsx(Text, {
      children: row.type && QUOTE_TYPE_MESSAGES[String(row.type)] ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: QUOTE_TYPE_MESSAGES[String(row.type)]
      }) : EMPTY_PLACEHOLDER
    })
  })
}, {
  columnName: 'acceptedDate',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "quotes.columns.acceptedDate"
  }),
  CellComponent: ({
    row
  }) => /*#__PURE__*/_jsx("td", {
    style: {
      whiteSpace: 'nowrap'
    },
    children: row.acceptedDate !== undefined ? /*#__PURE__*/_jsx(FormattedDateTime, {
      value: row.acceptedDate,
      format: "ll"
    }) : EMPTY_PLACEHOLDER
  })
}, {
  columnName: 'amount',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "quotes.columns.amount"
  }),
  CellComponent: ({
    row
  }) => /*#__PURE__*/_jsx("td", {
    style: {
      whiteSpace: 'nowrap'
    },
    children: /*#__PURE__*/_jsx(FormattedIntlCurrency, {
      value: Number(row.amount),
      currencyCode: row.currencyCode
    })
  })
}];
export function QuotesTable({
  loading,
  quotes
}) {
  const [sort, setSort] = useState(undefined);
  const handleSortChange = sorts => {
    setSort(sorts[0]);
  };
  const sortedQuotes = useMemo(() => sortQuotes(quotes, sort), [quotes, sort]);
  if (!loading && quotes.length === 0) {
    return /*#__PURE__*/_jsx(EmptyState, {
      flush: true,
      layout: "vertical",
      primaryContent: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quotes.empty"
      })
    });
  }
  return /*#__PURE__*/_jsx("div", {
    "data-test-id": "quotes-table",
    children: /*#__PURE__*/_jsx(PaginatedDataTable, {
      columns: COLUMNS,
      rows: sortedQuotes,
      getRowId: row => row.objectId.toString(),
      loading: loading,
      currentPage: 1,
      hasMore: false,
      hasPrev: false,
      onNextPage: () => undefined,
      onPrevPage: () => undefined,
      sorts: sort ? [sort] : [],
      onSortsChange: handleSortChange,
      totalResultsCount: sortedQuotes.length
    })
  });
}